import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import RouteFAQSection from './components/RouteFAQSection';
import RouteRecommendationSection from './components/RouteRecommendationSection';
import SocialShareButton from './components/SocialShareButton';
import Home from './pages/Home';
import EMICalculator from './pages/EMICalculator';
import SIPCalculator from './pages/SIPCalculator';
import LumpsumCalculator from './pages/LumpsumCalculator';
import PPFCalculator from './pages/PPFCalculator';
import EPFCalculator from './pages/EPFCalculator';
import NPSCalculator from './pages/NPSCalculator';
import FDCalculator from './pages/FDCalculator';
import RDCalculator from './pages/RDCalculator';
import GratuityCalculator from './pages/GratuityCalculator';
import HRACalculator from './pages/HRACalculator';
import IncomeTaxCalculator from './pages/IncomeTaxCalculator';
import GSTCalculator from './pages/GSTCalculator';
import SimpleInterestCalculator from './pages/SimpleInterestCalculator';
import CompoundInterestCalculator from './pages/CompoundInterestCalculator';
import SalaryCalculator from './pages/SalaryCalculator';
import InvoiceGenerator from './pages/InvoiceGenerator';
import BusinessPosterGenerator from './pages/BusinessPosterGenerator';
import RestaurantMenuGenerator from './pages/RestaurantMenuGenerator';
// Indian Government Saving Schemes
import SSYCalculator from './pages/SSYCalculator';
import NSCCalculator from './pages/NSCCalculator';
import KVPCalculator from './pages/KVPCalculator';
import SCSSCalculator from './pages/SCSSCalculator';
import POMISCalculator from './pages/POMISCalculator';
import APYCalculator from './pages/APYCalculator';
import POTDCalculator from './pages/POTDCalculator';
import PMVVYCalculator from './pages/PMVVYCalculator';
// Stock & Mutual Fund Calculators
import StockProfitCalculator from './pages/StockProfitCalculator';
import CAGRCalculator from './pages/CAGRCalculator';
import DividendYieldCalculator from './pages/DividendYieldCalculator';
import StockAverageCalculator from './pages/StockAverageCalculator';
import MutualFundReturnsCalculator from './pages/MutualFundReturnsCalculator';
import CapitalGainCalculator from './pages/CapitalGainCalculator';
import EcommerceProfitCalculator from './pages/EcommerceProfitCalculator';
import LoanEstimatorCalculator from './pages/LoanEstimatorCalculator';
import LICPlanCalculator from './pages/LICPlanCalculator';
// Advanced Calculators
import LoanPrepaymentCalculator from './pages/LoanPrepaymentCalculator';
import TDSCalculator from './pages/TDSCalculator';
import RetirementCalculator from './pages/RetirementCalculator';
import SWPCalculator from './pages/SWPCalculator';
import InflationCalculator from './pages/InflationCalculator';
import RentVsBuyCalculator from './pages/RentVsBuyCalculator';
import GoalSIPCalculator from './pages/GoalSIPCalculator';
import FIRECalculator from './pages/FIRECalculator';
import ELSSCalculator from './pages/ELSSCalculator';
import TDSRentCalculator from './pages/TDSRentCalculator';
import ProfessionalTaxCalculator from './pages/ProfessionalTaxCalculator';
import MarkupCalculator from './pages/MarkupCalculator';
import BreakevenCalculator from './pages/BreakevenCalculator';
import StampDutyCalculator from './pages/StampDutyCalculator';
import HomeLoanEligibilityCalculator from './pages/HomeLoanEligibilityCalculator';
import RentalYieldCalculator from './pages/RentalYieldCalculator';
import FixedMonthlyIncomeCalculator from './pages/FixedMonthlyIncomeCalculator';
import PetrolBillGenerator from './pages/PetrolBillGenerator';
import RentReceiptGenerator from './pages/RentReceiptGenerator';
import RestaurantBillGenerator from './pages/RestaurantBillGenerator';
import SalarySlipGenerator from './pages/SalarySlipGenerator';
import InternetBillGenerator from './pages/InternetBillGenerator';
import MedicalBillGenerator from './pages/MedicalBillGenerator';
import CabBillGenerator from './pages/CabBillGenerator';
import QuotationGenerator from './pages/QuotationGenerator';
import LibraryBookReceiptGenerator from './pages/LibraryBookReceiptGenerator';
import DailyHelperBillGenerator from './pages/DailyHelperBillGenerator';
import GeneralBillGenerator from './pages/GeneralBillGenerator';
import ShopBillGenerator from './pages/ShopBillGenerator';
import SchoolFeesReceiptGenerator from './pages/SchoolFeesReceiptGenerator';
import GroupCollectionReportGenerator from './pages/GroupCollectionReportGenerator';
import WaterStorageCalculator from './pages/ConstructionMaterialCalculator';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import { hasAnalyticsConfig, initAnalytics, trackPageView } from './utils/analytics';
import './App.css';

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (!hasAnalyticsConfig) {
      return;
    }

    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    trackPageView(pagePath);
  }, [location]);

  return null;
};

function App() {
  useEffect(() => {
    const url = new URL(window.location.href);
    const ampLikeParams = ['amp', 'amp_js_v', 'output'];
    let changed = false;

    ampLikeParams.forEach((param) => {
      if (!url.searchParams.has(param)) {
        return;
      }

      if (param === 'output') {
        const value = (url.searchParams.get(param) || '').toLowerCase();
        if (value !== 'amp') {
          return;
        }
      }

      url.searchParams.delete(param);
      changed = true;
    });

    if (!changed) {
      return;
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }, []);

  useEffect(() => {
    const syncRangeValue = (rangeInput) => {
      if (!(rangeInput instanceof HTMLInputElement) || rangeInput.type !== 'range') {
        return;
      }

      const min = Number(rangeInput.min || 0);
      const max = Number(rangeInput.max || 100);
      const value = Number(rangeInput.value || min);
      const denominator = max - min;
      const percentage = denominator > 0 ? ((value - min) / denominator) * 100 : 0;
      const clamped = Math.min(100, Math.max(0, percentage));

      rangeInput.style.setProperty('--value', `${clamped}%`);
    };

    const syncAllRanges = () => {
      document.querySelectorAll('input[type="range"]').forEach((input) => {
        syncRangeValue(input);
      });
    };

    const handleRangeInput = (event) => {
      syncRangeValue(event.target);
    };

    syncAllRanges();

    document.addEventListener('input', handleRangeInput, true);
    document.addEventListener('change', handleRangeInput, true);

    const observer = new MutationObserver(() => {
      syncAllRanges();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      document.removeEventListener('input', handleRangeInput, true);
      document.removeEventListener('change', handleRangeInput, true);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AnalyticsTracker />
        <div className="app">
          <Navbar />
          <div className="main-container">
            <Sidebar />
            <main className="content">
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/emi" element={<EMICalculator />} />
              <Route path="/sip" element={<SIPCalculator />} />
              <Route path="/lumpsum" element={<LumpsumCalculator />} />
              <Route path="/ppf" element={<PPFCalculator />} />
              <Route path="/epf" element={<EPFCalculator />} />
              <Route path="/nps" element={<NPSCalculator />} />
              <Route path="/fd" element={<FDCalculator />} />
              <Route path="/rd" element={<RDCalculator />} />
              <Route path="/gratuity" element={<GratuityCalculator />} />
              <Route path="/hra" element={<HRACalculator />} />
              <Route path="/income-tax" element={<IncomeTaxCalculator />} />
              <Route path="/gst" element={<GSTCalculator />} />
              <Route path="/simple-interest" element={<SimpleInterestCalculator />} />
              <Route path="/compound-interest" element={<CompoundInterestCalculator />} />
              <Route path="/salary" element={<SalaryCalculator />} />
              <Route path="/invoice" element={<InvoiceGenerator />} />
              <Route path="/business-poster" element={<BusinessPosterGenerator />} />
              <Route path="/restaurant-menu-generator" element={<RestaurantMenuGenerator />} />
              {/* Indian Government Saving Schemes */}
              <Route path="/ssy" element={<SSYCalculator />} />
              <Route path="/nsc" element={<NSCCalculator />} />
              <Route path="/kvp" element={<KVPCalculator />} />
              <Route path="/scss" element={<SCSSCalculator />} />
              <Route path="/pomis" element={<POMISCalculator />} />
              <Route path="/apy" element={<APYCalculator />} />
              <Route path="/potd" element={<POTDCalculator />} />
              <Route path="/pmvvy" element={<PMVVYCalculator />} />
              {/* Stock & Mutual Fund Calculators */}
              <Route path="/stock-profit" element={<StockProfitCalculator />} />
              <Route path="/cagr" element={<CAGRCalculator />} />
              <Route path="/dividend-yield" element={<DividendYieldCalculator />} />
              <Route path="/stock-average" element={<StockAverageCalculator />} />
              <Route path="/mf-returns" element={<MutualFundReturnsCalculator />} />
              <Route path="/capital-gain" element={<CapitalGainCalculator />} />
              <Route path="/ecommerce-profit" element={<EcommerceProfitCalculator />} />
              <Route path="/loan-estimator" element={<LoanEstimatorCalculator />} />
              <Route path="/lic-plan" element={<LICPlanCalculator />} />
              {/* Advanced Calculators */}
              <Route path="/loan-prepayment" element={<LoanPrepaymentCalculator />} />
              <Route path="/tds" element={<TDSCalculator />} />
              <Route path="/retirement" element={<RetirementCalculator />} />
              <Route path="/swp" element={<SWPCalculator />} />
              <Route path="/inflation" element={<InflationCalculator />} />
              <Route path="/rent-vs-buy" element={<RentVsBuyCalculator />} />
              <Route path="/goal-sip" element={<GoalSIPCalculator />} />
              <Route path="/fire" element={<FIRECalculator />} />
              <Route path="/elss" element={<ELSSCalculator />} />
              <Route path="/tds-rent" element={<TDSRentCalculator />} />
              <Route path="/professional-tax" element={<ProfessionalTaxCalculator />} />
              <Route path="/markup" element={<MarkupCalculator />} />
              <Route path="/breakeven" element={<BreakevenCalculator />} />
              <Route path="/stamp-duty" element={<StampDutyCalculator />} />
              <Route path="/home-loan-eligibility" element={<HomeLoanEligibilityCalculator />} />
              <Route path="/rental-yield" element={<RentalYieldCalculator />} />
              <Route path="/fixed-income" element={<FixedMonthlyIncomeCalculator />} />
              <Route path="/petrol-bill" element={<PetrolBillGenerator />} />
              <Route path="/rent-receipt" element={<RentReceiptGenerator />} />
              <Route path="/restaurant-bill" element={<RestaurantBillGenerator />} />
              <Route path="/salary-slip" element={<SalarySlipGenerator />} />
              <Route path="/internet-bill" element={<InternetBillGenerator />} />
              <Route path="/medical-bill" element={<MedicalBillGenerator />} />
              <Route path="/cab-bill" element={<CabBillGenerator />} />
              <Route path="/quotation" element={<QuotationGenerator />} />
              <Route path="/library-book-receipt" element={<LibraryBookReceiptGenerator />} />
              <Route path="/daily-helper-bill" element={<DailyHelperBillGenerator />} />
              <Route path="/general-bill" element={<GeneralBillGenerator />} />
              <Route path="/shop-bill" element={<ShopBillGenerator />} />
              <Route path="/school-fees-receipt" element={<SchoolFeesReceiptGenerator />} />
              <Route path="/group-collection-report" element={<GroupCollectionReportGenerator />} />
              <Route path="/water-storage" element={<WaterStorageCalculator />} />
            </Routes>
              <RouteRecommendationSection />
              <RouteFAQSection />
            </main>
          </div>
          <Footer />
          <SocialShareButton />
      </div>
    </Router>
    </HelmetProvider>
  );
}

export default App;
